import { Payload, redirect } from "redux-first-router";
import { ResourceTypeLink } from "@evergis/api";

import { getLocation } from "./selectors";
import { RouteName } from "../../appConfig/types";
import { ClassificationParameter, ClassifyType } from "../../interfaces/classification";
import { RootState } from "../reducers";

export interface LocationPayload extends Payload {
  project?: string;
  layer?: string;
  panel?: string;
  tab?: ResourceTypeLink;
  sharedMap?: string;
}

export type StyleLocationPayload = Pick<LocationPayload, "project" | "layer"> & {
  index?: number;
  isClusterSingleObject?: boolean;
};

export type GoToProjectPayload = {
  page?: number;
  editorCode?: Record<string, unknown>;
};

export type EqlLocationPayload = {
  tool?: string;
  onClickBack?: VoidFunction;
  onClickSave?: VoidFunction;
};

export interface ClassificationQuery {
  parameter?: ClassificationParameter;
  field?: string;
  count?: number;
  type?: ClassifyType;
  dialog?: string;
  valueIndex?: number;
  minQuantity?: number;
  maxQuantity?: number;
}

export interface CatalogQuery {
  addFromCatalog?: boolean;
  addAsBasemap?: boolean;
  openMap?: boolean;
  project?: string;
  types?: string[];
}

export const goToCatalogBase = (payload: LocationPayload, query?: CatalogQuery) => ({
  type: RouteName.Catalog,
  payload,
  query,
});

export const goToCatalog = (args: LocationPayload, query?: CatalogQuery) => (dispatch, getState) => {
  const { payload, query: locationQuery } = getLocation(getState());

  dispatch(goToCatalogBase(args, { ...query, project: locationQuery?.project || payload?.project }));
};

export const goToMainBase = (payload: LocationPayload) => ({
  type: RouteName.Main,
  payload,
});

export const goToStyleBase = (payload: StyleLocationPayload, query?: ClassificationQuery) => ({
  type: RouteName.Style,
  payload,
  query,
});

export const goToStyleEditorBase = (payload: StyleLocationPayload, query?: ClassificationQuery) => ({
  type: RouteName.StyleEditor,
  payload,
  query,
});

export const goToLayerConfigurationBase = (payload: LocationPayload) => ({
  type: RouteName.LayerConfiguration,
  payload,
});

export const goToProjectConfigurationBase = (payload: LocationPayload, query: GoToProjectPayload) => ({
  type: RouteName.ProjectConfiguration,
  payload,
  query,
});

export const goToEqlQueryBase = (payload: LocationPayload, query: EqlLocationPayload) => ({
  type: RouteName.EqlQuery,
  payload,
  query,
});

export const goToSymbolLibraryBase = (
  payload: StyleLocationPayload,
  query?: Pick<StyleLocationPayload, "isClusterSingleObject">,
) => ({
  type: RouteName.SymbolLibrary,
  payload,
  query,
});

export const goToMain = (args: LocationPayload) => (dispatch, getState) => {
  const { payload } = getLocation(getState());

  dispatch(goToMainBase({ ...payload, ...args }));
};

export const goToStyle = (args: StyleLocationPayload, query?: ClassificationQuery) => (dispatch, getState) => {
  const { payload } = getLocation(getState());

  dispatch(goToStyleBase({ ...payload, ...args }, query));
};

export const goToStyleEditor = (args: StyleLocationPayload, query?: ClassificationQuery) => (dispatch, getState) => {
  const { payload } = getLocation(getState());

  dispatch(goToStyleEditorBase({ ...payload, ...args }, query));
};

export const goToLayerConfiguration = (args: LocationPayload) => (dispatch, getState) => {
  const { payload } = getLocation(getState());

  dispatch(goToLayerConfigurationBase({ ...payload, ...args }));
};

export const goToProjectConfiguration =
  (args?: LocationPayload, query?: GoToProjectPayload) => (dispatch, getState) => {
    const { payload } = getLocation(getState());

    dispatch(goToProjectConfigurationBase({ ...payload, ...(args || {}) }, query));
  };

export const goToEqlQuery = (args?: LocationPayload, query?: EqlLocationPayload) => (dispatch, getState) => {
  const { payload } = getLocation(getState());

  dispatch(goToEqlQueryBase({ ...payload, onClickBack: null, onClickSave: null, ...(args || {}) }, query));
};

export const goToSymbolLibrary =
  (args: StyleLocationPayload, query?: Pick<StyleLocationPayload, "isClusterSingleObject">) => (dispatch, getState) => {
    const { payload } = getLocation(getState());

    dispatch(goToSymbolLibraryBase({ ...payload, ...args }, query));
  };

export const goToClassification = (query: ClassificationQuery) => dispatch => {
  dispatch(goToStyle({}, query));
};

export const goToPrevLocation = () => (dispatch, getState) => {
  const { prev } = getLocation(getState());

  if (prev && prev.payload) {
    dispatch(goToMain(prev.payload));
  }
};

export const mergeWithCurrentLocation =
  ({ type, payload = {}, query = {} }: Partial<RootState["location"]>) =>
  (dispatch, getState) => {
    const location = getLocation(getState());

    dispatch({
      payload: {
        ...location.payload,
        ...(payload as LocationPayload),
      },
      query: {
        ...location.query,
        ...query,
      },
      type: type || location.type,
    });
  };

export const redirectToCatalog = (payload: LocationPayload) => redirect(goToCatalogBase(payload));
